import { defineComponent as _defineComponent } from 'vue'
type ShortcutAction = (event: KeyboardEvent) => void;

/** ... */
interface ShortcutDefinition {
  label: string;
  keys: string[];
  action: ShortcutAction;
}

/** ... */
interface ShortcutHint {
  id: string;
  label: string;
  keys: { key: string; pressed: boolean }[];
}

/** ... */
type ShortcutMap = Record<string, ShortcutDefinition>;

/** ... */

import { ref, computed } from 'vue';

import { useEventListener } from '@composables';
import { modals } from '@modals';
import { useRouter } from '@router';
import { alert } from '@services/alert';
import { clipboard } from '@services/clipboard';
import { useStore } from '@store';
import { createEmailAlias } from '@tools/create-email-alias';

const __default__ = { name: 'KeyboardShortcutsUtil' };

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

/** ... */
const STANDARD_SHORTCUTS_MAP: ShortcutMap = {};

/** ... */
const DEVELOPMENT_SHORTCUTS_MAP: ShortcutMap = {
  toggleAppTheme: {
    label: 'Toggle App Theme',
    keys: ['Alt', 'KeyT'],
    action: toggleAppTheme,
  },
  toggleDashboardMode: {
    label: 'Toggle Dashboard Mode',
    keys: ['Alt', 'KeyM'],
    action: toggleDashboardMode,
  },
  insertTestEmailVariant: {
    label: 'Generate Test Email',
    keys: ['Alt', 'KeyE'],
    action: insertTestEmailVariant,
  },
  summonRoleSelectionModal: {
    label: 'Select Role',
    keys: ['Alt', 'KeyR'],
    action: summonRoleSelectionModal,
  },
  goToShop: {
    label: 'Go to Shop',
    keys: ['Alt', 'KeyS'],
    action: goToShopRoute,
  },
  goToTesting: {
    label: 'Go to Testing View',
    keys: ['Alt', 'Shift', 'KeyT'],
    action: goToTestingRoute,
  },
};

const SHORTCUT_MAP = { ...STANDARD_SHORTCUTS_MAP };

if (DEVELOPMENT) {
  Object.assign(SHORTCUT_MAP, DEVELOPMENT_SHORTCUTS_MAP);
}

const router = useRouter();
const store = useStore();

/** ... */
const activeKeys = ref<string[]>([]);

/** ... */
const hints = computed(() => {
  if (!activeKeys.value.length) return null;

  const shortcuts: ShortcutHint[] = [];

  const shiftKey = activeKeys.value.includes('Shift');
  const altKey = activeKeys.value.includes('Alt');
  const ctrlKey = activeKeys.value.includes('Control');

  for (const [id, shortcut] of Object.entries(SHORTCUT_MAP)) {
    if (
      (shiftKey && shortcut.keys.includes('Shift')) ||
      (altKey && shortcut.keys.includes('Alt')) ||
      (ctrlKey && shortcut.keys.includes('Control'))
    ) {
      const label = shortcut.label;

      const keys = shortcut.keys.map((key) => {
        key = key.replace(/^Key/, '');

        const pressed = activeKeys.value.includes(key);

        return { key, pressed };
      });

      shortcuts.push({ id, label, keys });
    }
  }

  return shortcuts.length ? shortcuts : null;
});

useEventListener('keydown', onKeyDown);
useEventListener('keyup', onKeyUp);

/**
 * `keydown` event listener for triggering shortcut actions.
 */
function onKeyDown(event: KeyboardEvent) {
  if (event.shiftKey && !activeKeys.value.includes('Shift')) {
    activeKeys.value.push('Shift');
  }

  if (event.altKey && !activeKeys.value.includes('Alt')) {
    activeKeys.value.push('Alt');
  }

  if (event.ctrlKey && !activeKeys.value.includes('Control')) {
    activeKeys.value.push('Control');
  }

  const matches = Object.values(SHORTCUT_MAP).filter(({ keys }) => {
    return keys.every((key) => {
      if (key === 'Shift') return event.shiftKey;
      if (key === 'Alt') return event.altKey;
      if (key === 'Control') return event.ctrlKey;

      return key === event.code;
    });
  });

  const action = matches.sort(({ keys }) => keys.length).reverse()[0]?.action;

  if (!action) return;

  // If a valid keypress was made, execute its action.

  event.preventDefault();

  action(event);
}

/**
 * `keyup` event listener.
 */
function onKeyUp(event: KeyboardEvent) {
  const keyIndex = activeKeys.value.findIndex((key) => key === event.key);

  if (keyIndex < 0) return;

  activeKeys.value.splice(keyIndex, 1);
}

//#region Shortcut Actions

/**
 * Toggle app theme.
 */
function toggleAppTheme() {
  let theme: ZephyrWeb.AppTheme;

  switch (store.state.theme) {
    case 'light':
      theme = 'dark';
      break;
    case 'dark':
      // theme = 'auto';
      theme = 'light';
      break;
    case 'auto':
      theme = 'light';
      break;
    default:
      theme = 'light';
  }

  store.commit('SET_THEME', { theme });

  alert.clear();
  alert.info(`Theme set to ${theme}.`);
}

/**
 * Toggle dashboard mode.
 */
function toggleDashboardMode() {
  let mode: ZephyrWeb.AppDashboardMode;

  switch (store.state.dashboardMode) {
    case 'regular':
      mode = 'fullwidth';
      break;
    case 'fullwidth':
      mode = 'regular';
      break;
    default:
      mode = 'regular';
  }

  store.commit('SET_DASHBOARD_MODE', { mode });

  alert.clear();
  alert.info(`Dashboard mode set to ${mode}.`);
}

/**
 * (DEVELOPMENT ONLY) Insert an auto-generated development email variant into
 * the currently-focused input field.
 */
function insertTestEmailVariant() {
  if (!process.env.DEV_TEST_EMAIL) {
    return alert.warning(
      'Could not insert test email variant: a development email has not been set. Please add the environment variable "ZEPHYR_DEV_TEST_EMAIL" with the desired email to your local environment.',
    );
  }

  // ...
  const email = createEmailAlias(
    process.env.DEV_TEST_EMAIL,
    (num) => `test-${num}`,
  );

  const activeEl = document.activeElement;

  if (!isHtmlInputElement(activeEl)) {
    return copyToClipboardAndAlert(
      email,
      'No valid input field was focused -- email copied to clipboard.',
    );
  }

  setTimeout(() => {
    activeEl.focus();

    activeEl.value = email;

    activeEl.dispatchEvent(new Event('input', { bubbles: true }));
  });
}

/**
 * Summon the role selection modal.
 */
function summonRoleSelectionModal() {
  void modals.account.selectRole();
}

function goToShopRoute() {
  void router.push({ name: 'shop' });
}

function goToTestingRoute() {
  void router.push({ name: 'testing' });
}

function copyToClipboardAndAlert(text: string, alertMessage: string) {
  void clipboard.copy(text);

  alert.info(alertMessage);
}

function isHtmlInputElement(
  el: unknown,
): el is HTMLInputElement | HTMLTextAreaElement {
  return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;
}

//#endregion Shortcut Actions

return { __sfc: true,STANDARD_SHORTCUTS_MAP, DEVELOPMENT_SHORTCUTS_MAP, SHORTCUT_MAP, router, store, activeKeys, hints, onKeyDown, onKeyUp, toggleAppTheme, toggleDashboardMode, insertTestEmailVariant, summonRoleSelectionModal, goToShopRoute, goToTestingRoute, copyToClipboardAndAlert, isHtmlInputElement }
}

})